<template>
  <div :class="['reader', `text-${textSize}`, `text-width-${textWidth}`]" :data-work-urn="workUrn" ref="reader">
    <div class="reader-wrapper">
      <div class="reader-header" :class="readerSlug" ref="reader-header">
        <h2>
          <BarToggle name="bar-toggle" class="browse" v-model="showWorkSelector" />
          <div class="heading">
            {{ workTitle }}
          </div>
          <span name="clone-reader" class="clone-reader" v-if="canClone" @click="onClone"><icon name="clone" /></span>
          <span name="close-reader" class="remove-reader" v-if="!showWorkSelector" @click="onRemove"><icon name="times-circle" /></span>
        </h2>
        <Paginator
          v-if="superheading"
          class="section-paginator"
          :next="nextSuperheadingFirstSection"
          :previous="previousSuperheadingFirstSection"
          @paginate="onPaginate"
        >
          <div class="title">{{ superheading.title }}</div>
        </Paginator>
        <ReaderWorkSelector v-if="showWorkSelector" :works="works" :initial-selected-work-urn="workUrn" @selected="onSelection" @close="onClose" />
      </div>
      <LoaderBall v-if="hasSelectedSection && sections.length == 0" />
      <div v-else :class="['text', { 'text-reuse': textReuseMode }]">
        <Superheading
          v-if="superheading.title && work"
          :work="work"
          :pivot-idx="pivotIdx"
          :sections-pager="sectionsPager"
          :selected-section="selectionSectionIdx"
          :selected-line="selectionLineIdx"
          :sections="sections"
          :selected-token="selectionTokenIdx"
          :alignments-map="alignmentsMap"
          :reader-id="readerId"
          @paginate="fetchData"
          @loadingPrevSection="onLoadingPrevSection"
          @scrollToElement="onScrollToElement"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import BarToggle from './BarToggle.vue';
  import ReaderWorkSelector from './ReaderWorkSelector.vue';
  import Paginator from './Paginator.vue';
  import LoaderBall from './LoaderBall.vue';
  import Superheading from './Superheading.vue';
  import mixins from '../mixins';
  import utils from '../utils';
  import { SOURCES_ROUTE_NAME, MAX_READER_SELECTIONS } from '../constants';

  export default {
    components: {
      BarToggle, ReaderWorkSelector, Superheading, Paginator, LoaderBall,
    },
    mixins: [mixins.RouteReaderSelectionsMixin, mixins.LocalizedWorkTitleMixin],
    props: ['readerId', 'selection', 'index'], // { workUrn, sectionIdx, lineIdx, tokenIdx }
    data() {
      return {
        pivotIdx: null,
        sectionsPager: {},
        showWorkSelector: false,
        superheading: {},
        works: [],
        sections: [],
      };
    },
    created() {
      this.fetchWorks();
      if (!this.selection.workUrn) {
        this.showWorkSelector = true;
      }
      if (this.$route.query.source === SOURCES_ROUTE_NAME) {
        this.showWorkSelector = true;
        // remove source from query params
        // so it is not copied when future calls to
        // $router.push are invoked with existing querystring
        // params, e.g. `RouteReaderSelectionsMixin.syncUrns`
        const query = {
          ...this.$route.query,
        };
        delete query.source;
        this.$router.replace({ query });
      }
    },
    watch: {
      selection: {
        immediate: true,
        handler(newValue, oldValue) {
          if (oldValue && newValue.id === oldValue.id) {
            return;
          }
          this.sections = [];
          this.fetchData({ sectionIdx: this.selectionSectionIdx });
        },
      },
      showWorkSelector: {
        immediate: true,
        handler() {
          if (this.showWorkSelector) {
            document.body.classList.add('modal-open');
          } else {
            document.body.classList.remove('modal-open');
          }
        },
      },
    },
    methods: {
      onPaginate(pos) {
        if (pos === null) {
          return;
        }
        const { firstSectionIdx } = this.superheadingFirstSections[pos];
        const content = { workUrn: this.selection.workUrn, sectionIdx: firstSectionIdx };
        const {
          workUrn,
          sectionIdx,
          lineIdx,
          tokenIdx,
        } = content;
        const id = utils.makeId(workUrn, sectionIdx, lineIdx, tokenIdx);
        const newSelection = utils.parseId(id);
        this.updateReader(this.readerId, newSelection);
      },
      onClone() {
        this.$emit('clone', this.readerId);
      },
      onClose() {
        this.showWorkSelector = false;
      },
      onSelection(selection) {
        // @@@ simplify the cast to / from id
        const content = { workUrn: selection.work, sectionIdx: selection.idx };
        const {
          workUrn,
          sectionIdx,
          lineIdx,
          tokenIdx,
        } = content;
        const id = utils.makeId(workUrn, sectionIdx, lineIdx, tokenIdx);
        const newSelection = utils.parseId(id);
        this.updateReader(this.readerId, newSelection);
      },
      onRemove() {
        this.removeReader(this.readerId);
      },
      onScrollToElement(element) {
        if (!element) {
          // don't scroll to the element if it doesn't yet exist
          // @@@ guard this elsewhere?
          return;
        }
        element.scrollIntoView();
        this.$refs.reader.scrollTop -= this.$refs['reader-header'].clientHeight;
      },
      onLoadingPrevSection(doLoad) {
        if (this.$refs.reader) {
          const initialHeight = this.$refs.reader.scrollHeight;
          const vm = this;
          doLoad().then(() => {
            vm.$nextTick(() => {
              vm.$refs.reader.scrollTop = vm.$refs.reader.scrollHeight - initialHeight;
            });
          });
        }
      },
      fetchWorks() {
        const query = `{
          works {
            edges {
              node {
                id
                urn
                name
                metadata
                superheadings {
                  edges {
                    node {
                      id
                      title
                      sections (first: 1) {
                        edges {
                          node {
                            idx
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }`;
        this.$gql(query).then((data) => {
          this.works = data.works.edges.map(e => ({
            ...e.node,
            workSlug: e.node.name.replace('ʾ', '').toLowerCase().split(' ')[0],
          }));
        });
      },
      fetchData({ sectionIdx, scrollingUp }) {
        if (!Number.isInteger(sectionIdx)) {
          // prevent a query when we have not yet populated a sectionIdx (e.g.,
          // within the works selector).
          return;
        }
        this.pivotIdx = sectionIdx;
        const oldScrollHeight = this.$refs.reader ? this.$refs.reader.scrollHeight : 0;

        // 0213IbnHisham.SiraNabawiyya.Shamela0023833-ara1.1
        const query = `{
          sections(work_Urn: "${this.workUrn}", idx: ${sectionIdx}) {
              edges {
                cursor
                node {
                  superheading {
                    id
                    title
                    firstSectionEdge: sections(first: 1) {
                      edges {
                        node {
                          idx
                        }
                      }
                    }
                    sections(work_Urn: "${this.workUrn}", idxPivot: ${sectionIdx}, pivotSize: 2) {
                      edges {
                        node {
                          idx
                          isSirah
                          label
                          lines {
                            edges {
                              node {
                                id
                                html
                                idx
                                kind
                                isSirah
                                textContent
                                tokens
                              }
                            }
                          }
                        }
                      }
                    }
                    lastSectionEdge: sections(last: 1) {
                      edges {
                        node {
                          idx
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `;
        this.$gql(query).then((data) => {
          const { superheading } = data.sections.edges[0].node;
          const newSections = superheading.sections.edges.map(e => ({
            ...e.node,
            lines: e.node.lines.edges.map(le => ({ ...le.node, sectionIdx: e.node.idx, workUrn: this.workUrn })),
          })).reduce((map, obj) => {
            map[obj.idx] = obj;
            return map;
          }, {});
          // @@@ don't render sections outside of the pivot, likely via
          // https://github.com/windfser/vue-recycler-list/
          const existingSections = this.sections.reduce((map, obj) => {
            map[obj.idx] = obj;
            return map;
          }, {});
          const allSections = { ...existingSections, ...newSections };
          this.sections = Object.keys(allSections).sort((a, b) => a - b).map(idx => allSections[idx]);
          this.superheading = {
            id: superheading.id,
            title: superheading.title,
          };
          if (this.$refs.reader && scrollingUp) {
            this.$nextTick(() => {
              this.$refs.reader.scrollTop = this.$refs.reader.scrollHeight - oldScrollHeight;
            });
          }
          this.updateSectionsPager(superheading);
        });
      },
      updateSectionsPager(superheadingNode) {
        // @@@ this query should be optimized and could possibly be cached
        // in the store
        const startSectionIdx = superheadingNode.firstSectionEdge.edges[0].node.idx;
        const endSectionIdx = superheadingNode.lastSectionEdge.edges[0].node.idx;
        const prevIdx = this.sections[0].idx;
        const nextIdx = this.sections[this.sections.length - 1].idx;
        this.sectionsPager = {
          prev: startSectionIdx < prevIdx ? prevIdx : undefined,
          next: endSectionIdx > nextIdx ? nextIdx : undefined,
        };
      },
    },
    computed: {
      readerNumber() {
        return this.index + 1;
      },
      workTitle() {
        return this.localizedWorkTitle(this.work);
      },
      work() {
        return this.works.find(w => w.urn === this.workUrn);
      },
      superheadingFirstSections() {
        return this.work && this.work.superheadings.edges.map(e => ({ superheading: e.node.id, firstSectionIdx: e.node.sections.edges[0].node.idx }));
      },
      previousSuperheadingFirstSection() {
        if (this.superheadingFirstSections === undefined) {
          return null;
        }
        const pos = this.superheadingFirstSections.findIndex(i => i.superheading === this.superheading.id);
        return pos === 0 ? null : pos - 1;
      },
      nextSuperheadingFirstSection() {
        if (this.superheadingFirstSections === undefined) {
          return null;
        }
        const pos = this.superheadingFirstSections.findIndex(i => i.superheading === this.superheading.id);
        return pos === (this.superheadingFirstSections.length - 1) ? null : pos + 1;
      },
      workUrn() {
        return this.selection.workUrn;
      },
      hasSelectedSection() {
        return Number.isInteger(this.selectionSectionIdx);
      },
      selectionSectionIdx() {
        return this.selection.sectionIdx;
      },
      selectionLineIdx() {
        return this.selection.lineIdx;
      },
      selectionTokenIdx() {
        return this.selection.tokenIdx;
      },
      canClone() {
        return !this.showWorkSelector && this.selections.length < MAX_READER_SELECTIONS;
      },
      alignmentsMap() {
        if (this.gqlData) {
          return this.gqlData.alignmentChunks.edges.map(e => e.node).reduce((map, obj) => {
            const lineIdx = obj.leftBoundary[0][0];
            if (map[lineIdx] === undefined) {
              map[lineIdx] = [];
            }
            map[lineIdx].push(obj);
            return map;
          }, {});
        }
        return {};
      },
      textReuseMatchThreshold() {
        return this.$store.state.textReuseMatchThreshold;
      },
      gqlQuery() {
        return this.work && this.work.id && this.superheading.id ? `{
          alignmentChunks(leftWork: "${this.work.id}", leftStart_Section_Superheading: "${this.superheading.id}", threshold_Gte: ${this.textReuseMatchThreshold}) {
            edges {
              node {
                id
                idx
                leftBoundary
                rightBoundary
                leftWork {
                  name
                  urn
                  id
                }
                rightWork {
                  name
                  urn
                }
              }
            }
          }
        }` : '';
      },
      textReuseMode() {
        return this.$store.state.textReuseMode;
      },
      textSize() {
        return this.$store.state.readerTextSize;
      },
      textWidth() {
        return this.$store.state.readerTextWidth;
      },
      readerSlug() {
        const color = this.$store.getters.readerColor(this.readerId);
        return `reader-${color}`;
      },
    },
  };
</script>

<style lang="scss">
  @import '../variables';

  #app[data-locale="ar"] .widget h3 {
    direction: rtl;
  }

  .reader {
    padding: 0 1em;
    width: 100%; // @@@ fix for IE
    overflow-y: scroll;
    margin-bottom: 10px;

    .reader-header {
      position: sticky;
      top: 0;
      background: $gray-100;
      h2,
      h3 {
        background: $gray-100;
        border: 1px solid $gray-200;
        padding: 10px;
        line-height: 1;
      }
      .clone-reader {
        cursor: pointer;
        opacity: 0.3;
        &:hover {
          opacity: 1;
        }
      }
      &.reader-1 h2 {
        color: $reader-1;
        .remove-reader {
          color: rgba($reader-1, 0.3);
          &:hover {
            color: $danger;
          }
        }
        .bar-toggle {
          color: rgba($reader-1, 0.3);;
          &:hover {
            color: $reader-1;
          }
          &.open,
          &.open:hover {
            color: $reader-1;
          }
        }
        background: rgba($reader-1, 0.1);
        border: 1px solid rgba($reader-1, 0.2);
      }

      &.reader-2 h2 {
        color: mix($reader-2, $black, 90%);
        .remove-reader {
          color: rgba($reader-2, 0.5);
          &:hover {
            color: $danger;
          }
        }
        .bar-toggle {
          color: rgba($reader-2, 0.8);
          &:hover {
            color: $reader-2;
          }
          &.open,
          &.open:hover {
            color: $reader-2;
          }
        }
        background: rgba($reader-2, 0.1);
        border: 1px solid rgba($reader-2, 0.4);
      }

      &.reader-3 h2 {
        color: $reader-3;
        .remove-reader {
          color: rgba($reader-3, 0.3);
          &:hover {
            color: $danger;
          }
        }
        .bar-toggle {
          color: rgba($reader-3, 0.3);;
          &:hover {
            color: $reader-3;
          }
          &.open,
          &.open:hover {
            color: $reader-3;
          }
        }
        background: rgba($reader-3, 0.1);
        border: 1px solid rgba($reader-3, 0.2);
      }

      .section-paginator {
        padding: 6px 8px 8px 8px;
        border: 1px solid $gray-300;
        background: $gray-100;
        color: $gray-500;
        font-weight: 500;
        font-size: 1.17em;
        margin: 0;
        border-top: none;
        .title {
          font-family: $ar-font-family;
        }
      }
      .heading {
        flex: 1;
        margin: auto 5px;
      }
      .bar-toggle {
        flex-grow: 0;
        margin-top: auto;
        margin-bottom: auto;
      };
      .remove-reader {
        flex-grow: 0;
        display: inline-block;
        color: $gray-400;
        cursor: pointer;
        &:hover {
          color: $danger;
        }
      }
    }

    .text {
      font-family: $ar-font-family;
      direction: rtl;
      margin-top: 20px;
      &.text-reuse {
        color: $gray-500;
      }
    }
    &.text-sm {
      .text {
        font-size: 20px;
        line-height: 1.6;
      }
    }

    &.text-md {
      .text {
        font-size: 24px;
        line-height: 1.7;
      }
    }

    &.text-lg {
      .text {
        font-size: 28px;
        line-height: 1.8;
      }
    }

    &.text-width-narrow {
      max-width: 25em;
    }
    &.text-width-normal {
      max-width: 35em;
    }
    &.text-width-wide {
      max-width: 45em;
    }
    &.text-width-full {
      max-width: 100%;
    }
  }

  // @@@ rtl implications
  #app[data-locale="ar"] ~ .tippy-popper .helptip-image .helptip-caption li {
    direction: rtl;
    text-align: right;
  }

  .helptip-image {
    img {
      width: 100%;
    }
    .helptip-caption {
      ol {
        margin: 0;
        padding: 5px 15px;
      }
      li {
        list-style-type: inherit;
        text-align: left;
      }
    }
  }

</style>
